// src/pages/inventory/InventoryOpeningManagement.js
// Inventario inicial - Aperturas de inventario

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Button, Table, Form, Spinner, Alert, Modal, Badge, InputGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import {
  getOpeningList,
  getOpeningById,
  getOpeningStatuses,
  createOpening,
  updateOpening,
  confirmOpening,
  cancelOpening
} from '../../services/inventoryOpeningApi';
import { getItems } from '../../services/itemApi';
import './InventoryOpeningManagement.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const formatDate = (str) => {
  if (!str) return '-';
  try {
    const d = new Date(str);
    return d.toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return str;
  }
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monthAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Nombre del item con atributos (peso, color, talla). Omite los que son N/A. Ej: "Chunky 1Kg" */
const getItemDisplayName = (item) => {
  if (!item) return '';
  const base = item.description || item.product?.name || item.product_name || '';
  const attrs = [];
  const w = item.weight_name || item.weight?.weight_name;
  if (w && String(w).toUpperCase() !== 'N/A') attrs.push(w);
  const c = item.color_name || item.color?.color_name;
  if (c && String(c).toUpperCase() !== 'N/A') attrs.push(c);
  const s = item.size_name || item.size?.size_name;
  if (s && String(s).toUpperCase() !== 'N/A') attrs.push(s);
  return attrs.length > 0 ? `${base} ${attrs.join(' ')}` : base;
};

const ItemImageThumb = ({ url, alt }) => {
  const [hasError, setHasError] = useState(false);
  const showImage = url && typeof url === 'string' && url.trim() && !hasError;
  return (
    <div className="opening-item-image-wrap">
      {showImage ? (
        <img src={url} alt={alt || ''} className="opening-item-img" onError={() => setHasError(true)} />
      ) : (
        <div className="opening-item-image-placeholder">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          </svg>
        </div>
      )}
    </div>
  );
};

// --- Modal Ver detalle ---
const DetailModal = ({ show, onHide, idOpening }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !idOpening) return;
    setLoading(true);
    setError('');
    getOpeningById(idOpening).then((res) => {
      if (res.ok) setDetail(res.data);
      else setError(res.data?.message || 'No se pudo cargar el detalle.');
      setLoading(false);
    });
  }, [show, idOpening]);

  const details = detail?.details ?? [];

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="inventory-opening-modal">
      <Modal.Header closeButton>
        <Modal.Title>Detalle de apertura #{idOpening}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="opening-loading">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {detail && !loading && (
          <>
            <div className="opening-detail-info">
              <div className="info-item"><span className="label">Fecha apertura</span><span>{formatDate(detail.open_date)}</span></div>
              <div className="info-item"><span className="label">Estado</span><Badge bg={detail.status_name === 'CONFIRMADO' ? 'success' : detail.status_name === 'CANCELADO' ? 'danger' : 'secondary'}>{detail.status_name}</Badge></div>
              <div className="info-item"><span className="label">Creado</span><span>{detail.created_at || '-'}</span></div>
              <div className="info-item"><span className="label">Notas</span><span>{detail.notes || '-'}</span></div>
            </div>
            <h6 className="mt-3 mb-2" style={{ color: '#332E5A', fontWeight: 600 }}>Detalles de ítems</h6>
            <div className="opening-table-wrap">
              <Table size="sm" striped bordered hover className="opening-detail-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Descripción</th>
                    <th>Producto</th>
                    <th>Cantidad</th>
                    <th>Costo unit.</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 && <tr><td colSpan={6} className="text-center">Sin detalles</td></tr>}
                  {details.map((d) => (
                    <tr key={d.id_opening_detail ?? d.id_item}>
                      <td>{d.sku || '-'}</td>
                      <td>{getItemDisplayName(d) || d.description || '-'}</td>
                      <td>{d.product_name || '-'}</td>
                      <td>{d.quantity ?? 0}</td>
                      <td>{formatterCOP.format(d.unit_cost ?? 0)}</td>
                      <td>{formatterCOP.format((d.quantity ?? 0) * (d.unit_cost ?? 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Nueva/Editar apertura ---
const FormModal = ({ show, onHide, opening, onSuccess, statuses, items }) => {
  const { user } = useAuth();
  const isEdit = !!opening?.id_opening;
  const isDraft = opening?.status_name === 'BORRADOR';

  const [openDate, setOpenDate] = useState(todayStr());
  const [idStatus, setIdStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [details, setDetails] = useState([{ id_item: '', quantity: 1, unit_cost: '', itemData: null }]);
  const [searchItem, setSearchItem] = useState('');
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  const filteredItems = useMemo(() => {
    const list = items || [];
    if (!searchItem.trim()) return list;
    const term = searchItem.toLowerCase();
    return list.filter(
      (it) =>
        (it.description || '').toLowerCase().includes(term) ||
        (it.product_name || it.product?.name || '').toLowerCase().includes(term) ||
        (it.sku || it.product?.sku || '').toLowerCase().includes(term)
    );
  }, [items, searchItem]);

  useEffect(() => {
    if (show) {
      if (opening?.id_opening) {
        setFetching(true);
        getOpeningById(opening.id_opening).then((res) => {
          if (res.ok && res.data) {
            const d = res.data;
            setOpenDate(d.open_date ? d.open_date.split(' ')[0] : todayStr());
            setIdStatus(String(d.id_status ?? ''));
            setNotes(d.notes ?? '');
            const det = d.details ?? [];
            setDetails(det.length > 0 ? det.map((x) => ({ id_item: x.id_item, quantity: x.quantity ?? 1, unit_cost: x.unit_cost ?? '', itemData: null })) : []);
          }
          setFetching(false);
        });
      } else {
        setOpenDate(todayStr());
        setIdStatus('');
        setNotes('');
        setDetails([]);
        setSearchItem('');
        setEditingRowIdx(null);
      }
      setError('');
    }
  }, [show, opening?.id_opening]);

  const handleSelectItem = (it) => {
    if (editingRowIdx !== null) {
      setDetails((d) => d.map((r, i) => (i === editingRowIdx ? { ...r, id_item: it.id_item, itemData: it } : r)));
      setEditingRowIdx(null);
    } else {
      setDetails((d) => [...d, { id_item: it.id_item, quantity: 1, unit_cost: it.cost_avg ?? it.cost_last ?? it.price ?? '', itemData: it }]);
    }
    setSearchItem('');
  };

  const removeRow = (idx) => {
    setDetails((d) => d.filter((_, i) => i !== idx));
    if (editingRowIdx === idx) setEditingRowIdx(null);
    else if (editingRowIdx !== null && editingRowIdx > idx) setEditingRowIdx(editingRowIdx - 1);
  };

  const updateRow = (idx, field, value) => {
    setDetails((d) => d.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const getItemDisplay = (detail) => {
    if (detail.itemData) return detail.itemData;
    return (items || []).find((i) => i.id_item === detail.id_item);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!user?.uid) {
      setError('Sesión no válida.');
      return;
    }
    const validDetails = details
      .map((r) => ({ id_item: parseInt(r.id_item, 10), quantity: parseInt(r.quantity, 10) || 0, unit_cost: parseFloat(r.unit_cost) || 0 }))
      .filter((r) => r.id_item && r.quantity > 0);
    if (validDetails.length === 0) {
      setError('Debe agregar al menos un ítem con cantidad mayor a 0.');
      return;
    }
    setLoading(true);
    const payload = {
      firebase_UID: user.uid,
      open_date: openDate,
      notes: notes.trim() || undefined,
      details: validDetails
    };
    if (!isEdit && idStatus) payload.id_status = parseInt(idStatus, 10);

    let res;
    if (isEdit && isDraft) {
      res = await updateOpening(opening.id_opening, payload);
    } else if (!isEdit) {
      res = await createOpening(payload);
    } else {
      setError('Solo se puede editar una apertura en BORRADOR.');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (res.ok && (res.status === 200 || res.status === 201 || res.code === 200 || res.code === 201)) {
      onSuccess?.();
      onHide();
    } else {
      setError(res.data?.message || 'Error al guardar.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="inventory-opening-modal">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Editar apertura' : 'Nueva apertura'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching && (
          <div className="opening-loading"><Spinner animation="border" /></div>
        )}
        <Form onSubmit={handleSubmit} className={fetching ? 'opacity-50' : ''}>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Fecha apertura <span className="text-danger">*</span></Form.Label>
            <Form.Control type="date" value={openDate} onChange={(e) => setOpenDate(e.target.value)} required />
          </Form.Group>
          {!isEdit && (
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select value={idStatus} onChange={(e) => setIdStatus(e.target.value)}>
                <option value="">CONFIRMADO (aplicar stock)</option>
                {statuses.map((s) => (
                  <option key={s.id_status} value={s.id_status}>{s.status_name} - {s.description}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Notas (opcional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Group>
          <div className="mb-3">
            <h6 className="mb-2" style={{ color: '#332E5A', fontWeight: 600 }}>Seleccionar ítem</h6>
            {editingRowIdx !== null && (
              <Alert variant="info" className="py-2 mb-2">
                Haz clic en "+" del ítem que deseas para reemplazar.
                <Button variant="link" size="sm" className="p-0 ms-2" onClick={() => setEditingRowIdx(null)}>Cancelar</Button>
              </Alert>
            )}
            <div className="opening-item-search-wrap">
              <InputGroup>
                <InputGroup.Text className="opening-search-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por SKU, descripción o producto..."
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                  className="opening-search-input"
                />
              </InputGroup>
            </div>
            <div className="opening-items-table-wrap">
              {filteredItems.length === 0 ? (
                <div className="opening-items-empty">
                  {!items?.length ? 'Cargando ítems...' : 'No hay ítems que coincidan con la búsqueda.'}
                </div>
              ) : (
                <Table size="sm" striped bordered hover className="opening-selector-table">
                  <thead>
                    <tr>
                      <th className="col-thumb">Imagen</th>
                      <th>SKU</th>
                      <th>Descripción</th>
                      <th>Peso</th>
                      <th>Color</th>
                      <th>Talla</th>
                      <th className="col-action">Agregar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => (
                      <tr key={it.id_item} className="opening-selector-row" onClick={() => handleSelectItem(it)}>
                        <td className="col-thumb">
                          <ItemImageThumb url={it.url || it.product?.url || it.product?.image_url || it.image_url} alt={getItemDisplayName(it)} />
                        </td>
                        <td>{it.sku || it.product?.sku || '-'}</td>
                        <td>{getItemDisplayName(it) || '-'}</td>
                        <td>{(it.weight_name || it.weight?.weight_name) && (it.weight_name || it.weight?.weight_name) !== 'N/A' ? (it.weight_name || it.weight?.weight_name) : '-'}</td>
                        <td>{it.color_name || it.color?.color_name || '-'}</td>
                        <td>{it.size_name || it.size?.size_name || '-'}</td>
                        <td className="col-action">
                          <Button type="button" variant="outline-primary" size="sm" onClick={(e) => { e.stopPropagation(); handleSelectItem(it); }}>
                            +
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>

          <div className="mb-3">
            <h6 className="mb-2" style={{ color: '#332E5A', fontWeight: 600 }}>Ítems agregados</h6>
            {details.length === 0 ? (
              <p className="opening-no-items-msg">Haz clic en el botón "+" para agregar ítems desde la tabla.</p>
            ) : (
              <div className="opening-details-form">
                {details.map((row, idx) => {
                  const itemDisplay = getItemDisplay(row);
                  return (
                    <div key={idx} className={`detail-row ${editingRowIdx === idx ? 'editing' : ''}`}>
                      <div className="detail-row-item">
                        <span className="detail-item-name">{itemDisplay ? (getItemDisplayName(itemDisplay) || `#${row.id_item}`) : `#${row.id_item}`}</span>
                        <span className="detail-item-sku">{(itemDisplay?.sku || itemDisplay?.product?.sku) ? `SKU: ${itemDisplay.sku || itemDisplay.product?.sku}` : ''}</span>
                        <Button type="button" variant="link" size="sm" className="detail-change-btn" onClick={() => setEditingRowIdx(idx)}>
                          Cambiar
                        </Button>
                      </div>
                      <Form.Control
                        type="number"
                        min="1"
                        placeholder="Cant."
                        value={row.quantity}
                        onChange={(e) => updateRow(idx, 'quantity', e.target.value)}
                        className="detail-qty"
                      />
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Costo unit."
                        value={row.unit_cost}
                        onChange={(e) => updateRow(idx, 'unit_cost', e.target.value)}
                        className="detail-cost"
                      />
                      <Button type="button" variant="outline-danger" size="sm" onClick={() => removeRow(idx)}>
                        ✕
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" onClick={onHide} disabled={fetching}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={loading || fetching}>{loading ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Confirmar ---
const ConfirmModal = ({ show, onHide, opening, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setError('');
    if (!user?.uid) {
      setError('Sesión no válida.');
      return;
    }
    setLoading(true);
    const res = await confirmOpening(opening.id_opening, user.uid);
    setLoading(false);
    if (res.ok) {
      onSuccess?.();
      onHide();
    } else {
      setError(res.data?.message || 'Error al confirmar.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="inventory-opening-modal">
      <Modal.Header closeButton>
        <Modal.Title>Confirmar apertura</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        <p>¿Desea confirmar la apertura #{opening?.id_opening}? Se aplicarán los movimientos de stock.</p>
        <div className="d-flex gap-2 justify-content-end">
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button variant="success" onClick={handleConfirm} disabled={loading}>{loading ? 'Confirmando…' : 'Confirmar'}</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- Modal Cancelar ---
const CancelModal = ({ show, onHide, opening, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCancel = async () => {
    setError('');
    if (!user?.uid) {
      setError('Sesión no válida.');
      return;
    }
    setLoading(true);
    const res = await cancelOpening(opening.id_opening, user.uid);
    setLoading(false);
    if (res.ok) {
      onSuccess?.();
      onHide();
    } else {
      setError(res.data?.message || 'Error al cancelar.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="inventory-opening-modal">
      <Modal.Header closeButton>
        <Modal.Title>Cancelar apertura</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        <p>¿Desea cancelar la apertura #{opening?.id_opening}? Se revertirá el stock aplicado.</p>
        <div className="d-flex gap-2 justify-content-end">
          <Button variant="outline-secondary" onClick={onHide}>No</Button>
          <Button variant="danger" onClick={handleCancel} disabled={loading}>{loading ? 'Cancelando…' : 'Sí, cancelar'}</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- Componente principal ---
const InventoryOpeningManagement = () => {
  const [openings, setOpenings] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: monthAgoStr(),
    date_to: todayStr(),
    id_status: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const limit = 20;

  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [alertMsg, setAlertMsg] = useState({ type: '', text: '' });

  const loadOpenings = useCallback(async (pageOverride) => {
    setLoading(true);
    const p = pageOverride ?? page;
    const params = { page: p, limit };
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.id_status) params.id_status = filters.id_status;
    const res = await getOpeningList(params);
    if (res.ok) {
      setOpenings(res.data ?? []);
      setTotalPages(res.total_pages ?? 1);
      setTotalRows(res.total_rows ?? 0);
    } else {
      setOpenings([]);
    }
    setLoading(false);
  }, [page, filters, limit]);

  const loadStatuses = useCallback(async () => {
    const res = await getOpeningStatuses();
    if (res.ok && Array.isArray(res.data)) setStatuses(res.data);
  }, []);

  const loadItems = useCallback(async () => {
    const res = await getItems();
    if (res.ok && res.data) {
      const raw = res.data;
      const list = raw?.message ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
      setItems(Array.isArray(list) ? list : []);
    }
  }, []);

  useEffect(() => { loadOpenings(); }, [loadOpenings]);
  useEffect(() => { loadStatuses(); }, [loadStatuses]);
  useEffect(() => { if (showForm) loadItems(); }, [showForm, loadItems]);

  const handleNew = () => {
    setSelectedOpening(null);
    setShowForm(true);
  };

  const handleView = (row) => {
    setSelectedOpening(row);
    setShowDetail(true);
  };

  const handleEdit = (row) => {
    setSelectedOpening(row);
    setShowForm(true);
  };

  const handleConfirm = (row) => {
    setSelectedOpening(row);
    setShowConfirm(true);
  };

  const handleCancel = (row) => {
    setSelectedOpening(row);
    setShowCancel(true);
  };

  const handleSuccess = () => {
    loadOpenings(1);
    setPage(1);
    setAlertMsg({ type: 'success', text: 'Operación realizada correctamente.' });
    setTimeout(() => setAlertMsg({}), 3000);
  };

  const getStatusBadge = (statusName) => {
    if (statusName === 'CONFIRMADO') return 'success';
    if (statusName === 'CANCELADO') return 'danger';
    return 'secondary';
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Helmet><title>Inventario inicial - DotoniWoof</title></Helmet>
      <Container fluid className="opening-page">
        <motion.div className="opening-header" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <h1>
            <span className="header-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </span>
            Inventario inicial
          </h1>
          <Button variant="primary" className="btn-new-opening" onClick={handleNew}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Nueva apertura
          </Button>
        </motion.div>

        {alertMsg.text && (
          <Alert variant={alertMsg.type} dismissible onClose={() => setAlertMsg({})} className="mb-3">
            {alertMsg.text}
          </Alert>
        )}

        <Card className="opening-card">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              type="date"
              value={filters.date_from}
              onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(1); }}
              className="filter-input"
            />
            <Form.Control
              type="date"
              value={filters.date_to}
              onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(1); }}
              className="filter-input"
            />
            <Form.Select
              value={filters.id_status}
              onChange={(e) => { setFilters((f) => ({ ...f, id_status: e.target.value })); setPage(1); }}
              className="filter-select"
            >
              <option value="">Todos los estados</option>
              {statuses.map((s) => (
                <option key={s.id_status} value={s.id_status}>{s.status_name}</option>
              ))}
            </Form.Select>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="opening-loading"><Spinner animation="border" /></div>
            ) : openings.length === 0 ? (
              <div className="opening-empty">
                <p>No hay aperturas de inventario.</p>
                <Button variant="primary" onClick={handleNew}>Nueva apertura</Button>
              </div>
            ) : (
              <>
                <div className="opening-table-wrap">
                  <Table responsive striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Estado</th>
                        <th>Notas</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openings.map((row) => (
                        <tr key={row.id_opening} className="clickable-row" onClick={() => handleView(row)}>
                          <td>{formatDate(row.open_date)}</td>
                          <td><Badge bg={getStatusBadge(row.status_name)}>{row.status_name}</Badge></td>
                          <td>{row.notes || '-'}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="opening-actions">
                              <Button variant="outline-primary" size="sm" onClick={() => handleView(row)}>Ver</Button>
                              {row.status_name === 'BORRADOR' && (
                                <>
                                  <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(row)}>Editar</Button>
                                  <Button variant="outline-success" size="sm" onClick={() => handleConfirm(row)}>Confirmar</Button>
                                </>
                              )}
                              {row.status_name === 'CONFIRMADO' && (
                                <Button variant="outline-danger" size="sm" onClick={() => handleCancel(row)}>Cancelar</Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="pagination-controls">
                  <Button size="sm" variant="outline-primary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                  <span className="mx-2">Pág. {page} / {totalPages} ({totalRows} registros)</span>
                  <Button size="sm" variant="outline-primary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        <DetailModal show={showDetail} onHide={() => setShowDetail(false)} idOpening={selectedOpening?.id_opening} />
        <FormModal show={showForm} onHide={() => setShowForm(false)} opening={selectedOpening} onSuccess={handleSuccess} statuses={statuses} items={items} />
        <ConfirmModal show={showConfirm} onHide={() => setShowConfirm(false)} opening={selectedOpening} onSuccess={handleSuccess} />
        <CancelModal show={showCancel} onHide={() => setShowCancel(false)} opening={selectedOpening} onSuccess={handleSuccess} />
      </Container>
    </>
  );
};

export default InventoryOpeningManagement;
